import {
  type FieldApi,
  type FormApi,
  type ValidationError,
} from '@tanstack/react-form';
import i18next, { exists, t } from 'i18next';
import { filter, isDeepEqual, map, pipe } from 'remeda';
import {
  z,
  ZodDefault,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodPipe,
  type ZodSchema,
  ZodString,
  ZodType,
} from 'zod';
import { de, en } from 'zod/locales';

import { formatFileSize } from './file.ts';
import { getFormattedNumber } from './l10n.ts';
import { parseApiNumber } from './number.ts';
import {
  clone,
  getNestedProperty,
  isObjectContentEqual,
  mapValueForKeys,
} from './object';

export const ValidUuidOrNotSetSchema = z
  .union([z.literal(''), z.uuid()])
  .nullish();

export const DateLikeSchema = z.union([z.string(), z.date()]);

/**
 * Translates decimal strings at API boundaries to numbers used by form state.
 */
export const DecimalStringCodec = z.codec(z.string().trim(), z.number(), {
  decode: (value, ctx) => {
    const number = parseApiNumber(value);

    if (number === null || !Number.isFinite(number)) {
      ctx.issues.push({
        code: 'custom',
        input: value,
        message: t('errors.invalidDecimalString', { ns: 'zodCustom' }),
      });

      return z.NEVER;
    }

    return number;
  },
  encode: String,
});

/**
 * Zod schema for validating an email address.
 * z.string().email() does not accept German umlaute in an email address although they are perfectly legal
 *
 * @deprecated Use `EmailWithUnicodeSchema`, which also accepts umlaute but refuses
 * malformed addresses such as `a..b@c.de` or `a@b.de.` that this pattern lets through.
 **/
export const EmailSchema = z
  .string()
  .trim()
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Ungültige E-Mail-Adresse'); // TODO: localize via the zodCustom key errors.invalidEmailAddress

/**
 * Zod's email rules with any Unicode letter or digit allowed, so addresses such as
 * `jürgen@bäcker.de` pass. The top-level domain may also be punycode, and a domain
 * label may not end with a hyphen.
 * Lengths are capped per RFC 5321: 64 characters before the @, 63 per domain label and
 * 254 in total. Unicode labels are counted in characters rather than in their longer
 * punycode form, so the caps are looser for them.
 * The error is resolved at validation time so it follows the active i18next language.
 */
export const EmailWithUnicodeSchema = z.email({
  error: () => t('errors.invalidEmailAddress', { ns: 'zodCustom' }),
  pattern:
    /^(?=.{1,254}$)(?=[^@]{1,64}@)(?!\.)(?!.*\.\.)[\p{L}\p{N}_'+\-.]*[\p{L}\p{N}_+-]@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+(?:\p{L}{1,63}|xn--[\p{L}\p{N}-]{0,58}[\p{L}\p{N}])$/u,
});

/**
 * Zod schema for validating an email address stored in the Vestigas database.
 * Our database limits email addresses to 64 characters before the @-sign!
 **/
export const EmailSchemaVestigasDBLimit = EmailSchema.refine(
  (value) => (value?.split('@')?.[0] ?? '').length <= 64,
  'Maximal 64 Zeichen vor dem @-Symbol',
);

/**
 * Zod schema for validating a domain name.
 * Accepts domain names with or without subdomains, e.g. gross-bau.com or www.gross-bau.com.
 */
export const DomainSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z0-9äöüß.-]+\.[a-z0-9äöüß.-]+$/i,
    'Ungültige Domain (z. B. gross-bau.com)',
  );

/**
 * Zod schema for loosely validating a phone number.
 * Accepts phone numbers with or without country code, parentheses, spaces, dashes, and slashes.
 */
export const PhoneNumberSchema = z
  .string()
  .trim()
  .regex(/^\+?[\d\s()\-/]+$/, 'Ungültige Telefonnummer'); // TODO: localize via the zodCustom key errors.invalidPhoneNumber

/**
 * Zod schema for UN/CEFACT units.
 * Since we don't have a complete list, we allow any 2-3 letter uppercase strings.
 * The error is resolved at validation time so it follows the active i18next language.
 */
export const UncefactUnitSchema = z
  .string()
  .regex(/^[a-z\d]{2,3}$/i, {
    error: () => t('errors.invalidUncefactUnit', { ns: 'zodCustom' }),
  })
  .toUpperCase();

/**
 * Zod schema for validating the uniqueness of an array of items.
 *
 * @param schema - The Zod schema to validate each item in the array.
 * @param [idSelector=(item) => item] - A function to select the unique identifier for each item.
 * @returns A Zod schema for an array with unique items.
 */
export const UniqueArraySchema = <Schema extends ZodSchema, K>(
  schema: Schema,
  idSelector: (item: Schema['_output']) => K = (item) => item,
) => {
  return z.array(schema).refine(
    (items) => {
      const ids = new Set<K>(items.map((item) => idSelector(item)));

      return ids.size === items.length;
    },
    // TODO: localize via the zodCustom key errors.arrayItemsMustBeUnique
    'Liste darf keine Duplikate enthalten',
  );
};

export const LiteralSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type Literal = z.infer<typeof LiteralSchema>;

export type Json = Literal | { [key: string]: Json } | Json[];

export const JsonSchema: ZodType<Json> = z.lazy(() =>
  z.union([
    LiteralSchema,
    z.array(JsonSchema),
    z.record(z.string(), JsonSchema),
  ]),
);

/**
 * Helper function to extract defaults from any Zod schema.
 *
 * @param schema - The Zod schema to extract defaults from.
 * @returns The defaults for the schema.
 */
export const getSchemaDefaults = <T>(schema: z.ZodJSONSchema<T>): T => {
  return schema.parse({});
};

/**
 * Returns the full field name by concatenating the prefix and the field name.
 * Useful for nested fields.
 *
 * @param fieldName - The name of the field.
 * @param namePrefix - The prefix of the field.
 * @return The full field name.
 */
export const getFieldName = (fieldName: string, namePrefix: string): string =>
  [namePrefix, fieldName].filter(Boolean).join('.').replaceAll('..', '.');

/**
 * Identifies and returns the names of form fields that have changed from their initial values.
 *
 * @param fieldNames - List of field names.
 * @param currentValues - The current values of the form fields.
 * @param initialValues - The initial values of the form fields.
 * @param filterFunction - A predicate function to filter out specific fields from the results.
 * @param mappingFunction - A function to transform the names of the fields in the results.
 * @returns An array of field names that have been modified.
 */
export const getUpdatedFieldsNames = ({
  fieldNames = [],
  currentValues = {},
  initialValues = {},
  filterFunction = () => true,
  mappingFunction = (fieldName) => fieldName,
}: {
  fieldNames: any[];
  currentValues: any;
  initialValues: any;
  filterFunction?: (fieldName: string) => boolean;
  mappingFunction?: (fieldName: string) => string;
}) =>
  fieldNames?.length
    ? pipe(
        fieldNames,
        filter((path) => {
          const currentValue = getNestedProperty(currentValues, path);
          const initialValue = getNestedProperty(initialValues, path);

          return !isDeepEqual(currentValue, initialValue);
        }),
        filter(filterFunction),
        map(mappingFunction),
        filter(Boolean),
      )
    : [];

type FlattenedError = {
  path: string[];
  message: string;
};

/**
 * Adds automatic logging to a TanStack Form-based form.
 * Returns a debounced listener object that logs changes, values, and errors to the logger.
 * @param formName - The name of the form.
 * @param logFunction - Logger object with an info method.
 * @param sanitizeOptions - Options for sanitizing sensitive field values in logs.
 * @param productAnalytics - Optional product analytics logging settings.
 * @returns A listener object for TanStack Form.
 *
 * @example
 * const form = useAppForm({
 *   defaultValues: {},
 *   onSubmit: () => {},
 *   validators: {},
 *   ...logFormChangesAndErrors(
 *     'UserForm',
 *     log,
 *     {
 *       // Will match 'password', 'user.password', 'users[0].password', etc.
 *       sensitiveFields: ['password', 'creditCard', 'token']
 *     }
 *   ),
 * });
 */
export const logFormChangesAndErrors = (
  formName: string,
  logger: {
    info: (payload: { message: string; data: Record<string, unknown> }) => void;
    productAnalyticsEvent?: (eventName: string, featureName: string) => void;
  },
  sanitizeOptions?: {
    sensitiveFields?: string[];
    sanitizeValue?: (fieldName: string, value: unknown) => unknown;
  },
  productAnalytics?: {
    featureName: string;
  },
) => {
  if (!logger) {
    return {};
  }

  if (!formName) {
    return {};
  }

  const { sensitiveFields = ['password'], sanitizeValue = () => '[REDACTED]' } =
    sanitizeOptions ?? {};

  return {
    listeners: {
      onChange({
        fieldApi,
        formApi,
      }: {
        fieldApi: FieldApi;
        formApi: FormApi;
      }) {
        const fieldName = fieldApi.name;
        const fieldValue = fieldApi.state.value;

        // Check if field is sensitive
        const isSensitive = sensitiveFields.some(
          (field) =>
            fieldName === field ||
            fieldName.endsWith(`.${field}`) ||
            fieldName.includes(`[${field}]`) ||
            fieldName.includes(`.${field}.`) ||
            fieldName.includes(`.${field}[`),
        );

        // Sanitize field value if sensitive
        const sanitizedFieldValue = isSensitive
          ? sanitizeValue(fieldName, fieldValue)
          : fieldValue;

        // Sanitize form values using our utility function
        const sanitizedFormValues = mapValueForKeys(
          formApi.state.values,
          sensitiveFields,
          (_key, value, path) => sanitizeValue(path, value),
        );

        // Log changes and state
        logger.info({
          message: 'Form value change',
          data: {
            errors: formApi.state.errors,
            fieldName,
            fieldValue: sanitizedFieldValue,
            formName,
            formValues: sanitizedFormValues,
          },
        });

        const shouldLogProductAnalyticsEvent =
          productAnalytics?.featureName &&
          typeof logger.productAnalyticsEvent === 'function';

        if (shouldLogProductAnalyticsEvent) {
          logger.productAnalyticsEvent!(
            `Change ${fieldName}`,
            productAnalytics.featureName,
          );
        }
      },
      onChangeDebounceMs: 500,
    },
  };
};

/**
 * Recursively flattens the errors returned by Tanstack Form or Zod.
 *
 * @param errors - The error object to flatten
 * @param path - The current path of keys leading to the error.
 * @returns An array of flattened errors with their paths and messages.
 */
export const flattenErrors = (
  errors: ValidationError | any[],
  path: string[] = [],
): FlattenedError[] => {
  let result: FlattenedError[] = [];

  // Handle array of error objects
  if (Array.isArray(errors)) {
    for (const error of errors) {
      for (const [, fieldErrors] of Object.entries(error)) {
        if (Array.isArray(fieldErrors)) {
          for (const fieldError of fieldErrors) {
            if (fieldError.message && fieldError.path) {
              if (fieldError.path.length > 1) {
                // If an error is assigned to multiple fields, we add it for each of them.
                for (const pathInstance of fieldError.path) {
                  result.push({
                    path: [pathInstance],
                    message: fieldError.message,
                  });
                }
              } else {
                result.push({
                  path: fieldError.path,
                  message: fieldError.message,
                });
              }
            }
          }
        }
      }
    }

    return result;
  }

  // Process errors at current level with _errors property
  if (
    errors._errors &&
    Array.isArray(errors._errors) &&
    errors._errors.length > 0
  ) {
    for (const message of errors._errors) {
      result.push({ path, message });
    }
  }

  // Process nested errors
  for (const [key, value] of Object.entries(errors)) {
    if (key !== '_errors' && typeof value === 'object' && value !== null) {
      const newPath = [...path, key];

      result = [...result, ...flattenErrors(value, newPath)];
    }
  }

  return result;
};

/**
 * Unwraps nested ZodOptional, ZodDefault, and ZodNullable schemas to get the core schema.
 *
 * @param schema - The schema to unwrap.
 * @return The unwrapped core schema.
 */
const unwrapSchema = (schema: ZodType): ZodType => {
  // Only unwrap ZodOptional, ZodDefault, and ZodNullable schemas
  // Don't unwrap arrays or other types that have unwrap methods
  if (
    schema instanceof ZodOptional ||
    schema instanceof ZodDefault ||
    schema instanceof ZodNullable
  ) {
    if ('unwrap' in schema && typeof schema.unwrap === 'function') {
      return unwrapSchema(schema.unwrap());
    }

    if ('innerType' in schema && typeof schema.innerType === 'function') {
      return unwrapSchema(schema.innerType());
    }
  }

  return schema;
};

/**
 * Retrieves the schema for a nested field.
 * Resolves paths like deliveryNote.lineItems[12].quantity.unit to retrieve the applicable schema.
 *
 * @param schema - The parent schema.
 * @param fieldPath - The path to the field, using bracket notation for arrays (e.g., lineItems[12].quantity).
 * @return The schema for the nested field, if found.
 */
const getNestedFieldSchema = (
  schema: ZodType,
  fieldPath: string,
): ZodType | undefined => {
  // Convert bracket notation to dot notation for uniform processing
  // Example: "lineItems[12].quantity.unit" becomes "lineItems.12.quantity.unit"
  const normalizedPath = fieldPath.replace(/\[(\d+)\]/g, '.$1');
  const pathSegments = normalizedPath
    .split('.')
    .filter((segment) => segment.length > 0);

  let currentSchema: ZodType | undefined = schema;

  for (const segment of pathSegments) {
    if (!currentSchema) {
      return undefined;
    }

    currentSchema = unwrapSchema(currentSchema);

    // Handle intersection schemas by checking both sides
    if (currentSchema instanceof z.ZodIntersection) {
      // Try to find the field in either side of the intersection
      const leftSchema = getNestedFieldSchema(currentSchema._def.left, segment);
      if (leftSchema) {
        currentSchema = leftSchema;
        continue;
      }

      const rightSchema = getNestedFieldSchema(
        currentSchema._def.right,
        segment,
      );
      if (rightSchema) {
        currentSchema = rightSchema;
        continue;
      }

      // If we didn't find it in either side, return undefined
      return undefined;
    }

    // Handle discriminated unions by checking all possible schemas
    if (currentSchema instanceof z.ZodDiscriminatedUnion) {
      // Try to find the field in any of the union's options
      // We check all options and return the first match
      const options = Array.from(currentSchema.options.values()) as ZodType[];
      let foundSchema: ZodType | undefined;

      for (const option of options) {
        const unwrappedOption = unwrapSchema(option);
        if (
          unwrappedOption instanceof ZodObject &&
          unwrappedOption.shape[segment]
        ) {
          foundSchema = unwrappedOption.shape[segment] as ZodType;
          break;
        }
      }

      // If we didn't find it in any option, return undefined
      if (!foundSchema) {
        return undefined;
      }

      currentSchema = foundSchema;
    } else if (currentSchema instanceof z.ZodUnion) {
      // Handle plain unions (e.g. z.union([existingItemSchema, newItemSchema]))
      // by resolving the segment in every option. Prefer an optional/nullable
      // match so that a field only counts as required when it is required in
      // every option that defines it.
      const matches = (currentSchema.options as ZodType[])
        .map((option) => getNestedFieldSchema(option, segment))
        .filter((match): match is ZodType => Boolean(match));

      if (matches.length === 0) {
        return undefined;
      }

      currentSchema =
        matches.find(
          (match) =>
            match instanceof ZodOptional ||
            match instanceof ZodDefault ||
            match instanceof ZodNullable,
        ) ?? matches[0];
    } else if (
      currentSchema instanceof ZodObject &&
      currentSchema.shape[segment]
    ) {
      currentSchema = currentSchema.shape[segment] as ZodType;
    } else if (currentSchema instanceof z.ZodArray && !isNaN(Number(segment))) {
      currentSchema = currentSchema.element;
    } else {
      return undefined;
    }
  }

  return currentSchema;
};

// The subset of Zod check metadata needed to decide whether a check rejects an
// empty value. Checks live on `schema._zod.def.checks`; each carries a def with
// a `check` discriminator plus kind-specific fields.
type ZodCheckInfo = {
  check: string;
  length?: number;
  minimum?: number | bigint;
  pattern?: RegExp;
};

/**
 * Whether a schema's checks reject an empty value ('' for strings, [] for
 * arrays), decided from check metadata alone. Parsing an empty value instead
 * (e.g. via safeParse) would execute every refinement during render and throws
 * on schemas with async refinements, so validation must never run here.
 * Opaque `custom` checks (.refine/.check) cannot be evaluated without running
 * them and therefore never mark a field as required.
 */
const checksRejectEmptyValue = (schema: ZodType): boolean => {
  const checks = (schema._zod.def.checks ?? []) as Array<{
    _zod: { def: ZodCheckInfo };
  }>;

  return checks.some(({ _zod: { def } }) => {
    switch (def.check) {
      case 'min_length':
        return Number(def.minimum) >= 1;
      case 'length_equals':
        return Number(def.length) >= 1;
      case 'string_format':
        // Formats carrying a pattern (regex, email, starts_with, …) are tested
        // against ''; formats without one (e.g. url) never accept an empty string.
        return def.pattern ? !def.pattern.test('') : true;
      default:
        return false;
    }
  });
};

/**
 * Determines if a field is required in a Zod schema.
 */
const isSchemaFieldRequired = (schema: ZodType, fieldPath: string): boolean => {
  const fieldSchema = getNestedFieldSchema(schema, fieldPath);

  if (!fieldSchema) {
    // Classify field as optional if we could not find the corresponding schema.
    return false;
  }

  // Classify field as optional if the field schema itself is optional or nullable.
  if (
    fieldSchema instanceof ZodOptional ||
    fieldSchema instanceof ZodDefault ||
    fieldSchema instanceof ZodNullable
  ) {
    return false;
  }

  // ZodPipe wraps schemas that use .transform() or .refine() chains.
  // Check the input side of the pipe for optionality, since that determines
  // whether the user must provide a value.
  if (fieldSchema instanceof ZodPipe) {
    const inputSchema = fieldSchema.in;

    return isSchemaFieldRequired(
      z.object({ __field: inputSchema as ZodType }),
      '__field',
    );
  }

  const unwrappedSchema = unwrapSchema(fieldSchema);

  // Text controls represent an empty value as '' and multi-selects as [], so
  // the required marker must reflect whether the field schema rejects that value.
  if (
    unwrappedSchema instanceof ZodString ||
    unwrappedSchema instanceof z.ZodArray
  ) {
    return checksRejectEmptyValue(unwrappedSchema);
  }

  return true;
};

const getFieldPathSegments = (fieldPath: string): string[] =>
  fieldPath
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

const omitFieldValue = (
  formValues: unknown,
  pathSegments: string[],
): unknown => {
  const fieldName = pathSegments.at(-1);
  if (
    fieldName === undefined ||
    typeof formValues !== 'object' ||
    formValues === null
  ) {
    return formValues;
  }

  const valuesWithoutField = clone(formValues);
  const parent = getNestedProperty<Record<string, unknown> | unknown[]>(
    valuesWithoutField as Record<string, unknown> | unknown[],
    pathSegments.slice(0, -1).join('.'),
  );

  if (parent) {
    delete (parent as Record<string, unknown>)[fieldName];
  }

  return valuesWithoutField;
};

const isConditionallyRequired = (
  schema: ZodType,
  fieldPath: string,
  formValues: unknown,
): boolean => {
  const pathSegments = getFieldPathSegments(fieldPath);
  const result = schema.safeParse(omitFieldValue(formValues, pathSegments));

  return (
    !result.success &&
    result.error.issues.some(
      (issue) =>
        issue.path.length === pathSegments.length &&
        issue.path.every(
          (segment, index) => String(segment) === pathSegments[index],
        ),
    )
  );
};

/**
 * The set of TanStack Form validators that can be checked for a field's schema.
 * The first validator present, in the order onChange, onBlur, onSubmit, onDynamic,
 * is treated as the "active" one.
 */
type FieldValidators = {
  onChange?: ZodType;
  onBlur?: ZodType;
  onSubmit?: ZodType;
  onDynamic?: ZodType;
};

/**
 * Determines if a field is required, either from a Zod schema directly, or from a
 * TanStack Form validators object. When given a validators object, the active validator
 * is resolved by checking onChange, onBlur, onSubmit, and onDynamic, in that order.
 *
 * @example: isFieldRequired(zodSchema, 'lineItems[1].quantity.amount')
 * @example: isFieldRequired(field.options.form.options.validators, field.name)
 *
 * @param validatorsOrSchema - A Zod schema, or a TanStack Form validators object, to check.
 * @param fieldPath - The path of the field to check, using bracket notation for arrays.
 * @param formValues - Current values used to resolve requirements declared by schema refinements.
 * @return Returns true if the field is required, false otherwise.
 */
export const isFieldRequired = (
  validatorsOrSchema: FieldValidators | ZodType | undefined,
  fieldPath: string,
  formValues?: unknown,
): boolean => {
  const schema =
    validatorsOrSchema instanceof ZodType
      ? validatorsOrSchema
      : (validatorsOrSchema?.onChange ??
        validatorsOrSchema?.onBlur ??
        validatorsOrSchema?.onSubmit ??
        validatorsOrSchema?.onDynamic);

  if (!schema) {
    return false;
  }

  return (
    isSchemaFieldRequired(schema, fieldPath) ||
    (formValues !== undefined &&
      isConditionallyRequired(schema, fieldPath, formValues))
  );
};

type ZodLocaleError = ReturnType<typeof de>['localeError'];

// Comparator symbol for a size issue, based on the kind and whether it is inclusive.
const sizeComparator = (
  kind: 'tooBig' | 'tooSmall',
  inclusive: boolean,
): string => {
  if (kind === 'tooBig') {
    return inclusive ? '<=' : '<';
  }

  return inclusive ? '>=' : '>';
};

// Translates a Zod internal type/origin name (e.g. "string") via the `zodCustom`
// namespace, falling back to the raw name when no translation is provided.
const translateZodType = (name: string): string =>
  t(`types.${name}`, { defaultValue: name, ns: 'zodCustom' });

// Unit noun for a size issue (e.g. "characters" / "Zeichen"), or null when the
// origin has no associated unit and the message should read "…to be X" instead.
const translateSizeUnit = (origin: string): string | null =>
  exists(`sizable.${origin}`, { ns: 'zodCustom' })
    ? t(`sizable.${origin}`, { ns: 'zodCustom' })
    : null;

// Builds a fully-localized message for a size-based issue (`too_big` / `too_small`).
const formatSizeIssue = (params: {
  kind: 'tooBig' | 'tooSmall';
  origin: string;
  bound: number | bigint;
  inclusive: boolean;
}): string => {
  const { kind, origin, bound, inclusive } = params;
  const comparator = sizeComparator(kind, inclusive);

  // A raw byte count (e.g. "<=20971520 Bytes") is unreadable; a file's own
  // size deserves its own template, scaled to KB/MB/GB via `formatFileSize`.
  if (origin === 'file') {
    return t('issues.fileSize', {
      comparator,
      limit: formatFileSize(Number(bound)),
      ns: 'zodCustom',
    });
  }

  const values = {
    comparator,
    limit: getFormattedNumber({ value: String(bound) }),
    origin: translateZodType(origin),
  };
  const unit = translateSizeUnit(origin);

  return unit
    ? t(`issues.${kind}Sized`, { ...values, ns: 'zodCustom', unit })
    : t(`issues.${kind}`, { ...values, ns: 'zodCustom' });
};

/**
 * The "required" message the Zod error map shows for missing values and empty
 * strings. Pass it as a rule's `error` where a stricter rule should read the same,
 * e.g. `z.string().min(2, { error: getRequiredFieldMessage })`; as a callback it
 * follows the language active at validation time.
 */
export const getRequiredFieldMessage = (): string =>
  t('issues.stringRequired', { ns: 'zodCustom' });

/**
 * Builds a single, language-agnostic error map for all Zod validation across every
 * consuming app. All copy lives in each app's `zodCustom` namespace and is resolved
 * through `t()`, which follows the active i18next language — so one map serves every
 * locale and reacts to runtime language switches, with no per-language config or
 * hardcoded strings. Issue codes we don't template here fall back to Zod's own
 * localized copy.
 *
 * Register once per app: `z.config(createZodErrorMap())`.
 *
 * @returns A Zod locale config object with a language-aware `localeError`.
 */
export const createZodErrorMap = (): ReturnType<typeof de> => {
  // Zod's built-in localized fallbacks for issue codes we don't template ourselves.
  const bases = { de: de(), en: en() } as const;

  const localeError: ZodLocaleError = (issue) => {
    /*
     * "Required" surfaces two ways: a missing value (invalid_type against
     * undefined/null) and an empty string validated with min(1). Map both to one
     * concise, centrally-translated message.
     */
    const isMissingValue =
      issue.code === 'invalid_type' &&
      (issue.input === undefined || issue.input === null);
    const isEmptyRequiredString =
      issue.code === 'too_small' &&
      issue.origin === 'string' &&
      Number(issue.minimum) === 1;

    if (isMissingValue || isEmptyRequiredString) {
      return getRequiredFieldMessage();
    }

    switch (issue.code) {
      case 'invalid_type':
        return t('issues.invalidType', {
          expected: translateZodType(issue.expected),
          ns: 'zodCustom',
        });

      case 'too_big':
        return formatSizeIssue({
          bound: issue.maximum,
          inclusive: Boolean(issue.inclusive),
          kind: 'tooBig',
          origin: issue.origin,
        });

      case 'too_small':
        return formatSizeIssue({
          bound: issue.minimum,
          inclusive: Boolean(issue.inclusive),
          kind: 'tooSmall',
          origin: issue.origin,
        });

      default:
        return (
          i18next.language.startsWith('en') ? bases.en : bases.de
        ).localeError(issue);
    }
  };

  return { localeError };
};

/**
 * Represents the operations needed to sync array items with an API.
 * Used for forms that manage arrays of items where items can be created, updated, or deleted and each change has to be saved individually using dedicated API endpoints.
 *
 * @template TPayload - The type for create/update payloads (without server-managed fields)
 */
export type ArrayItemOperations<TPayload> = {
  toCreate: TPayload[]; // Items without id that need to be created
  toUpdate: Array<{ itemId: string; payload: TPayload }>; // Items with id that have changed and need to be updated
  toDelete: string[]; // Item IDs that were in the original but are not in current (need deletion)
};

/**
 * Configuration options for computing array item operations.
 *
 * @template TExistingItem - The type for existing items (with id and server-managed fields)
 * @template TNewItem - The type for new items (without id)
 * @template TPayload - The type for create/update payloads
 */
export type ComputeArrayItemOperationsOptions<
  TExistingItem extends { id: string },
  TNewItem extends object,
  TPayload,
> = {
  /**
   * Fields to exclude when comparing items for changes.
   * Typically includes server-managed fields like 'id', 'createdOn', 'modifiedOn'.
   */
  fieldsToExclude: readonly string[];

  /**
   * Extracts the payload from an item by removing server-managed fields.
   * This is used to prepare items for create/update API calls.
   */
  extractPayload: (item: TExistingItem | TNewItem) => TPayload;

  /**
   * Type guard to check if an item is an existing item (has an id).
   * Default implementation checks for non-empty string 'id' property.
   */
  isExistingItem?: (item: TExistingItem | TNewItem) => item is TExistingItem;
};

/**
 * Default type guard to check if an item has a valid id.
 */
const defaultIsExistingItem = <
  TExistingItem extends { id: string },
  TNewItem extends object,
>(
  item: TExistingItem | TNewItem,
): item is TExistingItem => {
  if (!('id' in item)) {
    return false;
  }

  const id = (item as { id: unknown }).id;

  return typeof id === 'string' && id.length > 0;
};

/**
 * Prepares an item for comparison by removing specified fields.
 */
const prepareForComparison = <T extends Record<string, unknown>>(
  item: T,
  fieldsToExclude: readonly string[],
): Partial<T> => {
  const copy = clone(item);

  for (const field of fieldsToExclude) {
    if (Object.hasOwn(copy, field)) {
      delete copy[field];
    }
  }

  return copy;
};

/**
 * Checks if an item has changed compared to its original version.
 * Uses order-independent comparison for arrays within items.
 */
const hasItemChanged = <T extends Record<string, unknown>>(
  currentItem: T,
  originalItem: T,
  fieldsToExclude: readonly string[],
) => {
  const currentComparable = prepareForComparison(currentItem, fieldsToExclude);
  const originalComparable = prepareForComparison(
    originalItem,
    fieldsToExclude,
  );

  return !isObjectContentEqual(currentComparable, originalComparable);
};

/**
 * Computes the operations needed to sync current form items with the server.
 *
 * This is a generic utility for forms that manage arrays of items where:
 * - New items (without id) need to be created
 * - Existing items (with id) that changed need to be updated
 * - Items removed from the array need to be deleted
 *
 * @param currentItems - The current items from the form state
 * @param originalItemsMap - Map of original item IDs to their data (from initial load)
 * @param options - Configuration options for the operation
 * @returns Object containing arrays of items to create, update, and delete
 *
 * @example
 * // For contract items
 * const operations = computeArrayItemOperations(
 *   form.state.values.items,
 *   originalItemsMap,
 *   {
 *     fieldsToExclude: ['id', 'contractId', 'createdOn', 'modifiedOn', 'quantityCalledOff'],
 *     extractPayload: (item) => {
 *       const { id, contractId, createdOn, modifiedOn, ...payload } = item;
 *       return payload;
 *     },
 *   }
 * );
 *
 * // Execute operations
 * await Promise.all(operations.toDelete.map(id => deleteItem(parentId, id)));
 * await Promise.all(operations.toCreate.map(item => createItem(parentId, item)));
 * await Promise.all(operations.toUpdate.map(({ itemId, payload }) =>
 *   updateItem(parentId, itemId, payload)
 * ));
 */
export function computeArrayItemOperations<
  TExistingItem extends { id: string },
  TNewItem extends object,
  TPayload,
>(
  currentItems: Array<TExistingItem | TNewItem>,
  originalItemsMap: Map<string, TExistingItem>,
  options: ComputeArrayItemOperationsOptions<TExistingItem, TNewItem, TPayload>,
): ArrayItemOperations<TPayload> {
  const {
    fieldsToExclude,
    extractPayload,
    isExistingItem = defaultIsExistingItem<TExistingItem, TNewItem>,
  } = options;

  const currentItemIdsArray: string[] = [];
  for (const item of currentItems) {
    if (isExistingItem(item)) {
      currentItemIdsArray.push(item.id);
    }
  }
  const currentItemIds = new Set(currentItemIdsArray);

  // Items to create: items without an id
  const toCreate: TPayload[] = [];
  for (const item of currentItems) {
    if (!isExistingItem(item)) {
      toCreate.push(extractPayload(item));
    }
  }

  // Items to update: items with id that exist in original and have changed
  const toUpdate: Array<{ itemId: string; payload: TPayload }> = [];
  for (const item of currentItems) {
    if (!isExistingItem(item)) {
      continue;
    }

    const original = originalItemsMap.get(item.id);

    if (!original) {
      continue;
    }

    if (hasItemChanged(item, original, fieldsToExclude)) {
      toUpdate.push({
        itemId: item.id,
        payload: extractPayload(item),
      });
    }
  }

  // Items to delete: IDs in original that are not in current
  const toDelete = Array.from(originalItemsMap.keys()).filter(
    (id) => !currentItemIds.has(id),
  );

  return {
    toCreate,
    toDelete,
    toUpdate,
  };
}
